import {
  Form,
  FormValueSetter,
  SearchInput,
  SETTER_ACTIONS,
} from "@neru/base/components";
import { useFilter, useNotifications, useTags } from "@neru/base/hooks";
import { type FilterProps } from "@neru/base/types";
import GAEvents from "@neru/base/utils/ga-events";
import type { WithChildren } from "@neru/types";
import { Badge, H1, Phrase } from "@neru/ui";
import { useEffect } from "react";

import * as S from "./styles";

enum FILTER_VIEWS {
  name = "name",
  tag = "ProductVersion.VersionTags.Tag.name",
}

interface FormValues {
  [FILTER_VIEWS.tag]: Array<string>;
  [FILTER_VIEWS.name]: string;
}

const FILTER_RULES = {
  every: [FILTER_VIEWS.tag],
  ilike: [FILTER_VIEWS.name],
};

export const Filter = ({ children, onFilter }: FilterProps & WithChildren) => {
  const { data, error, loading } = useTags();
  const [filter, setValues, flatFilter] = useFilter<FormValues>(FILTER_RULES);
  const { showError } = useNotifications();

  useEffect(() => {
    if (!loading && error) {
      showError("Failed to get filter options. Retrying...");
    }
  }, [error, loading, showError]);

  useEffect(() => {
    if (filter) {
      if (filter.name) {
        const { name, ...rest } = filter;

        GAEvents.search(String(name));

        onFilter({
          _and: {
            _and: {
              ...rest,
              _or: [
                { name },
                {
                  ProductVersions: {
                    long_description: name,
                  },
                },
                {
                  ProductVersions: {
                    short_description: name,
                  },
                },
              ],
            },
          },
        });
      } else {
        GAEvents.filterData("Products");
        onFilter({
          _and: {
            _and: {
              ...filter,
            },
          },
        });
      }
    }
  }, [filter, onFilter]);

  const hasTag = (tag: string) => flatFilter?.[FILTER_VIEWS.tag]?.includes(tag);

  return (
    <Form name="products" onFinish={setValues}>
      <S.PageBanner>
        <div className="parts p1" />
        <div className="parts p2" />
        <div className="parts p3" />
        <div className="parts p4" />
        <div className="parts p5" />
        <div className="parts p6">🚀</div>
        <div className="parts p7" />
        <div className="parts p8" />
        <div className="parts p9" />
        <div className="parts p10" />
        <div className="parts p11">🤖</div>
        <div className="parts p12" />
        <div className="parts p13" />
        <H1 className="!text-[66px] font-normal text-white">
          Code Hub
          <span style={{ verticalAlign: "super" }}>
            <Badge connotation="success" layout="soft" shape="pill">
              beta
            </Badge>
          </span>
        </H1>
        <Phrase className="mx-8 px-8 text-center font-bold text-primary-0">
          Get started with pre-built solutions using Vonage's serverless
          platform.
        </Phrase>

        <S.Row justifyContent="center" marginTop="sm" paddingTop="xs">
          <S.Column flexGrow={1} maxWidth={600} minWidth={400}>
            <SearchInput
              name={FILTER_VIEWS.name}
              placeholder="What do you want to build?"
              value={flatFilter?.name}
            />
          </S.Column>
        </S.Row>

        {data?.Tags.length ? (
          <S.Row alignItems="center" justifyContent="center" marginTop="sm">
            <FormValueSetter name={FILTER_VIEWS.tag} submit>
              <Badge
                connotation={flatFilter?.[FILTER_VIEWS.tag] ? "primary" : "cta"}
                icon="check-circle-solid"
                layout={flatFilter?.[FILTER_VIEWS.tag] ? "soft" : "filled"}
                shape="pill"
              >
                All Products
              </Badge>
            </FormValueSetter>

            <S.Box marginX="sm">
              <Phrase className="text-primary-700">|</Phrase>
            </S.Box>
            {data?.Tags.map(({ name }) => (
              <S.Box key={name} marginX="xs">
                <FormValueSetter
                  action={
                    hasTag(name) ? SETTER_ACTIONS.remove : SETTER_ACTIONS.insert
                  }
                  name={FILTER_VIEWS.tag}
                  submit
                  value={name}
                >
                  <Badge
                    connotation={hasTag(name) ? "cta" : "primary"}
                    icon={hasTag(name) ? "check-line" : "plus-line"}
                    layout={hasTag(name) ? "filled" : "soft"}
                    shape="pill"
                  >
                    {name}
                  </Badge>
                </FormValueSetter>
              </S.Box>
            ))}
          </S.Row>
        ) : null}
      </S.PageBanner>
      <S.Row marginTop="xl">
        <S.Column flexGrow={2}>{children}</S.Column>
      </S.Row>
    </Form>
  );
};
